/**
 * TasksListPanel
 *
 * 세부실행과제 목록(`TasksTable`)을 사이드패널 안에서 보여주는 래퍼.
 * 대시보드의 표·그래프 클릭 시 필터링된 세부실행과제 목록을 보여주기 위한 공용 컴포넌트.
 *
 * 필터: 패널 상단에 ListPanelFilterBar(세부실행과제상태/성과상태/사업/부서/담당자/검색).
 * - 부모가 prop으로 넘긴 필터는 lock(disabled)으로 표시.
 * - 패널 안에서 사용자가 변경한 필터는 패널-로컬 state로만 적용 (부모 page state는 영향 없음).
 */

import React, { useEffect, useState } from 'react'
import SidePanel from '@/components/ui/SidePanel'
import TasksTable from './TasksTable'
import ListPanelFilterBar from './ListPanelFilterBar'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import type { TaskFullResponse } from '@/api/generated/models/TaskFullResponse'

export interface TasksListPanelProps {
  isOpen: boolean
  onClose: () => void
  /** 사이드패널 헤더 타이틀. 기본값: "세부실행과제 목록" */
  title?: string
  /** 사이드패널 너비. 기본 1000 */
  width?: number
  /** z-index. 기본 40 (상세 패널이 위에 뜨도록 detail의 기본 50보다 낮춤) */
  zIndex?: number

  // ===== 사전 적용 필터 (lock 됨) — 정의되면 disabled로 표시되고 변경 불가 =====
  workspaceId: string
  selectedBusinessId?: string
  selectedBusinessName?: string
  selectedDepartmentId?: string
  selectedDepartmentName?: string
  selectedMemberId?: string
  selectedMemberName?: string
  startDate?: Date | null
  endDate?: Date | null
  /** 검색은 lock 불가 — 초기값으로만 사용 */
  searchQuery?: string
  searchTrigger?: number
  /** 세부실행과제상태 'incomplete' | 'completed' — 정의되면 lock */
  status?: string
  /** 성과상태 'incomplete' | 'completed' — 정의되면 lock */
  performanceStatus?: string
  isActionButtonClicked?: boolean
  refreshTrigger?: number

  // ===== 콜백 =====
  onTaskClick?: (task: TaskTreeNode) => void
  onReportClick?: (task: TaskTreeNode) => void
  onDataRefreshed?: (tasks: TaskFullResponse[]) => void
}

// 세부실행과제상태·성과상태 공통 선택지 ('' = 전체는 필터 바가 붙인다)
const COMPLETION_OPTIONS = [
  { value: 'incomplete', label: '미완료' },
  { value: 'completed', label: '완료' },
]

const TasksListPanel: React.FC<TasksListPanelProps> = ({
  isOpen,
  onClose,
  title = '세부실행과제 목록',
  width = 1000,
  zIndex = 40,
  workspaceId,
  selectedBusinessId,
  selectedBusinessName,
  selectedDepartmentId,
  selectedDepartmentName,
  selectedMemberId,
  selectedMemberName,
  startDate,
  endDate,
  searchQuery,
  searchTrigger,
  status,
  performanceStatus,
  isActionButtonClicked,
  refreshTrigger,
  onTaskClick,
  onReportClick,
  onDataRefreshed,
}) => {
  // ===== 패널-로컬 필터 state (lock된 필드는 사용하지 않음) =====
  const [panelStatus, setPanelStatus] = useState('')
  const [panelPerformanceStatus, setPanelPerformanceStatus] = useState('')
  const [panelBusinessId, setPanelBusinessId] = useState('')
  const [panelBusinessName, setPanelBusinessName] = useState('')
  const [panelDepartmentId, setPanelDepartmentId] = useState('')
  const [panelDepartmentName, setPanelDepartmentName] = useState('')
  const [panelMemberId, setPanelMemberId] = useState('')
  const [panelMemberName, setPanelMemberName] = useState('')
  const [searchInput, setSearchInput] = useState(searchQuery || '')
  const [panelSearchQuery, setPanelSearchQuery] = useState(searchQuery || '')
  const [panelSearchTrigger, setPanelSearchTrigger] = useState(0)

  // 패널이 닫히면 로컬 필터 초기화 (다음에 다시 열 때 깨끗한 상태)
  useEffect(() => {
    if (!isOpen) {
      setPanelStatus('')
      setPanelPerformanceStatus('')
      setPanelBusinessId('')
      setPanelBusinessName('')
      setPanelDepartmentId('')
      setPanelDepartmentName('')
      setPanelMemberId('')
      setPanelMemberName('')
      setSearchInput(searchQuery || '')
      setPanelSearchQuery(searchQuery || '')
    }
  }, [isOpen, searchQuery])

  const handleSearchSubmit = () => {
    setPanelSearchQuery(searchInput.trim())
    setPanelSearchTrigger(t => t + 1)
  }

  // 효과적 필터 = lock prop ?? panel-local state
  const effectiveStatus = status ?? (panelStatus || undefined)
  const effectivePerformanceStatus = performanceStatus ?? (panelPerformanceStatus || undefined)
  const effectiveBusinessId = selectedBusinessId ?? (panelBusinessId || undefined)
  const effectiveDepartmentId = selectedDepartmentId ?? (panelDepartmentId || undefined)
  const effectiveMemberId = selectedMemberId ?? (panelMemberId || undefined)

  return (
    <SidePanel isOpen={isOpen} onClose={onClose} title={title} width={width} zIndex={zIndex}>
      <div className="flex flex-col h-full">
        <ListPanelFilterBar
          workspaceId={workspaceId}
          showStatus
          statusLabel="세부실행과제상태"
          statusOptions={COMPLETION_OPTIONS}
          lockedStatus={status}
          status={panelStatus}
          onStatusChange={setPanelStatus}
          performanceStatusOptions={COMPLETION_OPTIONS}
          lockedPerformanceStatus={performanceStatus}
          performanceStatus={panelPerformanceStatus}
          onPerformanceStatusChange={setPanelPerformanceStatus}
          lockedBusinessId={selectedBusinessId}
          lockedBusinessName={selectedBusinessName}
          businessId={panelBusinessId}
          businessName={panelBusinessName}
          onBusinessChange={(id, name) => { setPanelBusinessId(id); setPanelBusinessName(name) }}
          lockedDepartmentId={selectedDepartmentId}
          lockedDepartmentName={selectedDepartmentName}
          departmentId={panelDepartmentId}
          departmentName={panelDepartmentName}
          onDepartmentChange={(id, name) => { setPanelDepartmentId(id); setPanelDepartmentName(name) }}
          lockedMemberId={selectedMemberId}
          lockedMemberName={selectedMemberName}
          memberId={panelMemberId}
          memberName={panelMemberName}
          onMemberChange={(id, name) => { setPanelMemberId(id); setPanelMemberName(name) }}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchSubmit={handleSearchSubmit}
        />
        <div className="p-4 flex-1 overflow-hidden">
          <TasksTable
            workspaceId={workspaceId}
            selectedBusinessId={effectiveBusinessId}
            selectedDepartmentId={effectiveDepartmentId}
            selectedMemberId={effectiveMemberId}
            isVisible={isOpen}
            startDate={startDate}
            endDate={endDate}
            searchQuery={panelSearchQuery}
            searchTrigger={(searchTrigger || 0) + panelSearchTrigger}
            status={effectiveStatus}
            performanceStatus={effectivePerformanceStatus}
            isActionButtonClicked={isActionButtonClicked}
            refreshTrigger={refreshTrigger}
            onTaskClick={onTaskClick}
            onReportClick={onReportClick}
            onDataRefreshed={onDataRefreshed}
          />
        </div>
      </div>
    </SidePanel>
  )
}

export default TasksListPanel
